import React, { useRef, useState, useEffect } from "react";

import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";

//Unform e componentes
import { Form } from "@unform/web";
import UCheckbox from "@/frontend/componentes/unform/UCheckbox";
import { MessageConfirmDelete } from "@/frontend/componentes/Message";
import Paginacao from "@/frontend/componentes/Paginacao";
import TextoFormatado from "@/frontend/componentes/TextoFormatado";

//Context
import { useAuth } from "@/frontend/context/auth";

//Common
import { ID_TIPO_NOTIFICACAO } from "@/common/constantes";
import { fn_capitalizarPalavra } from "@/common/utils/util";
import {
  fn_formatarData,
  fn_formatarDataHora,
  fn_getDataHoraAtual,
} from "@/common/utils/date";

//Util
import {
  fn_getIconeOrdenacao,
  ICONES,
  ICONES_BOTAO,
  ICONES_MENU,
  ICONES_MENSAGEM,
  ICONES_MENU_ITEM,
  ICONES_SETA,
} from "@/frontend/utils/icones";
import { fn_getUsuario } from "@/frontend/utils/sessao";
import {
  fn_alterarNotificacoesRecolhidas,
  fn_getNotificacoesRecolhidas,
  fn_salvarNotificacoesRecolhidas,
} from "@/frontend/utils/notificacao";
import { MENSAGENS_ERRO, showMensagemErro } from "@/frontend/utils/mensagens";

//API
import { api_listarPage, ROTA_LISTAR } from "@/frontend/services/api/api_list";
import { api_salvar, ROTA_SALVAR } from "@/frontend/services/api/api_iud";
import { api_getCountNotificacaoNaoLida } from "@/frontend/services/api/api_others";

function TNotificacao_usuario({
  modalOpen,
  toggleModal,
  countNotifNaoLida,
  setCountNotifNaoLida,
}) {
  const { sessaoUsuario } = useAuth();
  const usuarioLogado = fn_getUsuario(sessaoUsuario);

  //### USE REF ###
  const formRef = useRef(null);
  const rowsRef = useRef([]);

  //### USE STATE ###
  const [pageNumber, setPageNumber] = useState(1);
  const [result, setResult] = useState({
    pages: 0,
    count: 0,
    items: [],
    offset: 0,
    limit: 0,
  });

  const [selectedRow, setSelectedRow] = useState(null);
  const [checkAcaoTodos, setCheckAcaoTodos] = useState(false);
  const [checkAcao, setCheckAcao] = useState({});
  const [acaoExcluir, setAcaoExcluir] = useState(false);
  const [acaoMarcarLido, setAcaoMarcarLido] = useState(false);
  const [countSelecionados, setCountSelecionados] = useState(0);

  const [campoOrder, setCampoOrder] = useState("notificacao,data");
  const [direcaoOrder, setDirecaoOrder] = useState("DESC");

  const [keyAtualizarPage, setKeyAtualizarPage] = useState(0);

  //Ids das notificações lidas que o usuário recolheu (guardadas no navegador)
  const [recolhidas, setRecolhidas] = useState(() =>
    usuarioLogado ? fn_getNotificacoesRecolhidas(usuarioLogado.id) : [],
  );

  //### USE EFFECT ###
  useEffect(() => {
    if (usuarioLogado) {
      //Carrega as notificações do usuário logado com paginação
      async function loadNotificacoes(page) {
        const filtroOrder = campoOrder
          ? `&order=${campoOrder},${direcaoOrder}`
          : "";

        const result = await api_listarPage(
          ROTA_LISTAR.NOTIFICACAO_USUARIO,
          page,
          `id_usuario=${usuarioLogado.id}&situacao=A${filtroOrder}`,
        );
        setResult(result);

        const countNaoLido = await api_getCountNotificacaoNaoLida(
          usuarioLogado.id,
        );
        setCountNotifNaoLida(countNaoLido);

        if (!selectedRow) {
          setSelectedRow(0);
        } else if (selectedRow >= result.items.length) {
          setSelectedRow(result.items.length - 1);
        }

        definirStatusCheckAcaoTodos(result);
      }

      loadNotificacoes(pageNumber || 1);
    }
  }, [pageNumber, campoOrder, direcaoOrder, keyAtualizarPage]);

  //Foca na linha selecionada sempre que ela for atualizada
  useEffect(() => {
    if (
      result.items.length > 0 &&
      selectedRow !== null &&
      rowsRef.current[selectedRow]
    ) {
      rowsRef.current[selectedRow].focus();
    }
  }, [result, selectedRow]);

  //Ao selecionar a ação
  useEffect(() => {
    const checkMarcado = Object.values(checkAcao).filter(
      (item) => item === true,
    );

    setCountSelecionados(checkMarcado.length);
  }, [checkAcao]);

  //### FUNCOES ###
  //Define o status do check Todos
  function definirStatusCheckAcaoTodos(result, checked) {
    let countSelecionados = checked ? 1 : 0;
    result.items.forEach((nu) => {
      if (checkAcao[nu.id] == true) {
        countSelecionados++;
      }
    });

    //Se todos os itens da página estiverem marcados
    if (countSelecionados == result.items.length) {
      //marca o selecionar todos
      setCheckAcaoTodos(true);
    } else {
      //desmarca o selecionar todos
      setCheckAcaoTodos(false);
    }
  }

  //Salva a data da visualização da notificação do usuário
  async function salvarDataVisualizacao(notificacaoUsuario) {
    const ret = await api_salvar(
      ROTA_SALVAR.NOTIFICACAO_USUARIO,
      notificacaoUsuario.id,
      { data_visualizacao: fn_getDataHoraAtual(true) },
      false,
    );
    if (ret) {
      setKeyAtualizarPage((prev) => prev + 1);
    }
  }

  //Exclui a notificação do usuário
  async function excluirNotificacao(idNu) {
    const ret = await api_salvar(ROTA_SALVAR.NOTIFICACAO_USUARIO, idNu, {
      situacao: "I",
    });

    if (ret) {
      if (result.items.length == 1) {
        const page = result.pages > 1 ? result.pages - 1 : 1;
        setPageNumber(page);
      }
      setKeyAtualizarPage((prev) => prev + 1);
    }
  }

  //Retorna o ícone e a classe do ícone conforme o tipo da notificação
  //Ex: relógio nos pagamentos a vencer sem posse; aviso (warning) nos vencidos; erro na solicitação rejeitada e no pagamento com erro
  function getIconeNotificacao(notificacaoUsuario) {
    const idTipo = notificacaoUsuario.notificacao?.id_notificacao_tipo;

    if (idTipo == ID_TIPO_NOTIFICACAO.POSSE_DOCUMENTO_PENDENTE) {
      return {
        icone: ICONES_MENSAGEM.PRAZO,
        classe: " notificacao-icone-prazo",
      };
    }

    if (idTipo == ID_TIPO_NOTIFICACAO.POSSE_DOCUMENTO_VENCIDA) {
      return {
        icone: ICONES_MENSAGEM.AVISO,
        classe: " notificacao-icone-alerta",
      };
    }

    if (
      idTipo == ID_TIPO_NOTIFICACAO.SOLICITACAO_REJEITADA ||
      idTipo == ID_TIPO_NOTIFICACAO.PAGAMENTO_ERRO
    ) {
      return { icone: ICONES_MENSAGEM.ERRO, classe: " notificacao-icone-erro" };
    }

    return { icone: ICONES_MENU_ITEM.NOTIFICACAO, classe: "" };
  }

  //Retorna se a notificação aparece recolhida (só o título): a não lida ou a lida que o usuário recolheu
  function isNotificacaoRecolhida(notificacaoUsuario) {
    return (
      !notificacaoUsuario.data_visualizacao ||
      recolhidas.includes(String(notificacaoUsuario.id))
    );
  }

  //Recolhe (true) ou abre (false) as notificações e guarda no navegador
  function alterarRecolhidas(idsNotificacaoUsuario, recolher) {
    const novasRecolhidas = fn_alterarNotificacoesRecolhidas(
      recolhidas,
      idsNotificacaoUsuario,
      recolher,
    );

    setRecolhidas(novasRecolhidas);
    fn_salvarNotificacoesRecolhidas(usuarioLogado.id, novasRecolhidas);
  }

  //Ids das notificações lidas da página (só a lida pode ser recolhida e aberta)
  function getIdsNotificacoesLidasPagina() {
    return result.items.filter((nu) => nu.data_visualizacao).map((nu) => nu.id);
  }

  //### EVENTOS ###
  //OnClick - Recolher / abrir a notificação lida
  function handleClickRecolher(event, notificacaoUsuario) {
    //Não seleciona a linha nem dispara a ação da linha
    event.stopPropagation();

    alterarRecolhidas(
      [notificacaoUsuario.id],
      !isNotificacaoRecolhida(notificacaoUsuario),
    );
  }

  //OnClick - Recolher todas / Abrir todas (notificações lidas da página)
  function handleBotaoRecolherTodas(recolher) {
    alterarRecolhidas(getIdsNotificacoesLidasPagina(), recolher);
  }

  //OnClick - Linha selecionada
  async function handleClickRow(rowIndex) {
    setSelectedRow(rowIndex);
  }

  //OnKeyDown - Linha
  async function handleKeyDownRow(event) {
    const notificacaoUs = result.items[selectedRow];

    if (event.key == "Enter") {
      event.preventDefault();
      //Não lida: marca como lida (e abre). Lida: só abre ou recolhe, sem gravar
      if (!notificacaoUs.data_visualizacao) {
        await salvarDataVisualizacao(notificacaoUs);
      } else {
        alterarRecolhidas(
          [notificacaoUs.id],
          !isNotificacaoRecolhida(notificacaoUs),
        );
      }
    } else if (event.key == "Delete") {
      const result = await MessageConfirmDelete(
        null,
        "da notificação selecionada",
      );

      if (result == 1) {
        await excluirNotificacao(notificacaoUs.id);
      }
    } else if (event.key === "ArrowUp" && selectedRow > 0) {
      setSelectedRow((prev) => prev - 1);
    } else if (
      event.key === "ArrowDown" &&
      selectedRow < result.items.length - 1
    ) {
      setSelectedRow((prev) => prev + 1);
    }
  }

  //OnClick - Ordenação coluna
  function handleOrdenacao(nomeCampo) {
    if (campoOrder == nomeCampo) {
      setDirecaoOrder(direcaoOrder && direcaoOrder == "ASC" ? "DESC" : "ASC");
    } else {
      setCampoOrder(nomeCampo);
      setDirecaoOrder("ASC");
    }
  }

  //OnChange - Check Ação Todos
  function handleChangeCheckAcaoTodos(event) {
    setCheckAcaoTodos(event.target.checked);

    //Marca os checks individuais conforme o check todos
    result.items.forEach((nu) => {
      if (acaoExcluir || (acaoMarcarLido && !nu.data_visualizacao)) {
        setCheckAcao((prev) => ({
          ...prev,
          [nu.id]: event.target.checked,
        }));
      }
    });
  }

  //OnChange - Check Ação Individual
  function handleChangeCheckAcaoInd(event, id) {
    setCheckAcao((prev) => ({
      ...prev,
      [id]: !checkAcao[id],
    }));

    //Se estiver desmarcando algum check individual
    if (event.target.checked == false) {
      //Desmarca o check excluir todos
      setCheckAcaoTodos(false);
    } else {
      definirStatusCheckAcaoTodos(result, event.target.checked);
    }
  }

  //OnClick - Botão Ação (Salvar, Excluir...)
  async function handleBotaoSalvarAcao() {
    const listIDNu = Object.entries(checkAcao)
      .filter(([idNu, valor]) => valor === true)
      .map(([idNu]) => idNu);

    if (listIDNu.length == 0) {
      showMensagemErro(MENSAGENS_ERRO.SELECIONE_PELOMENOSUMA, "notificação");
    } else {
      let ret = null;

      if (acaoExcluir) {
        ret = await api_salvar(
          ROTA_SALVAR.NOTIFICACAO_USUARIO,
          listIDNu.join(","),
          {
            situacao: "I",
          },
        );
      } else if (acaoMarcarLido) {
        ret = await api_salvar(
          ROTA_SALVAR.NOTIFICACAO_USUARIO,
          listIDNu.join(","),
          { data_visualizacao: fn_getDataHoraAtual(true) },
        );
      }

      if (ret) {
        setCheckAcao({});
        setCheckAcaoTodos(false);
        setAcaoExcluir(false);
        setAcaoMarcarLido(false);

        if (result.items.length == 1) {
          const page = result.pages > 1 ? result.pages - 1 : 1;
          setPageNumber(page);
        }
        setKeyAtualizarPage((prev) => prev + 1);
      }
    }
  }

  //OnClick - Botão Excluir
  function handleBotaoExcluir() {
    setAcaoExcluir(!acaoExcluir);
  }
  //OnClick - Botão Excluir
  function handleBotaoMarcarLido() {
    setAcaoMarcarLido(!acaoMarcarLido);
  }

  //OnClick - Botão Fechar
  function handleBotaoFechar() {
    toggleModal();
  }
  //SUBMIT
  function handleSubmit(data) {}

  return (
    <div>
      <Modal
        isOpen={modalOpen}
        toggle={() => toggleModal()}
        backdrop="static"
        size="xl"
      >
        <Form ref={formRef} onSubmit={handleSubmit}>
          <ModalHeader toggle={toggleModal} className="titulo-tela-modal">
            Notificações
          </ModalHeader>

          <ModalBody>
            {/* RESUMO, ORDENAÇÃO E AÇÕES */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div className="d-flex align-items-center gap-2">
                <span className="notificacao-resumo">
                  {countNotifNaoLida}{" "}
                  {countNotifNaoLida == 1 ? "não lida" : "não lidas"}
                </span>
              </div>

              <div className="d-flex align-items-center gap-2">
                <span className="texto-menor text-muted">Ordenar por</span>
                {[
                  { campo: "notificacao,data", label: "Data" },
                  { campo: "notificacao,notificacao_tipo,nome", label: "Tipo" },
                  { campo: "data_visualizacao", label: "Leitura" },
                ].map((ordem) => (
                  <Button
                    key={ordem.campo}
                    size="sm"
                    color="light"
                    className={
                      campoOrder == ordem.campo
                        ? "notificacao-ordem notificacao-ordem-ativa"
                        : "notificacao-ordem"
                    }
                    onClick={() => handleOrdenacao(ordem.campo)}
                  >
                    {ordem.label}
                    {fn_getIconeOrdenacao(
                      campoOrder,
                      direcaoOrder,
                      ordem.campo,
                    )}
                  </Button>
                ))}

                <UncontrolledDropdown>
                  <DropdownToggle color="light" size="sm">
                    {ICONES_MENU.ACOES}
                  </DropdownToggle>

                  <DropdownMenu end>
                    <DropdownItem
                      onClick={() => handleBotaoMarcarLido()}
                      disabled={acaoExcluir || result.items.length == 0}
                    >
                      <div className="d-flex">
                        <div>{ICONES.CHECK}</div>
                        <div className="ms-2">
                          Marcar como lido{" "}
                          {acaoMarcarLido ? "(Ativado)" : ""}{" "}
                        </div>
                      </div>
                    </DropdownItem>

                    <DropdownItem
                      onClick={() => handleBotaoExcluir()}
                      disabled={acaoMarcarLido || result.items.length == 0}
                    >
                      <div className="d-flex">
                        <div>{ICONES_BOTAO.EXCLUIR}</div>
                        <div className="ms-2">
                          Excluir {acaoExcluir ? "(Ativado)" : ""}{" "}
                        </div>
                      </div>
                    </DropdownItem>

                    <DropdownItem divider />

                    <DropdownItem
                      onClick={() => handleBotaoRecolherTodas(true)}
                      disabled={getIdsNotificacoesLidasPagina().length == 0}
                    >
                      <div className="d-flex">
                        <div>{ICONES_SETA.FECHAR_DETALHE}</div>
                        <div className="ms-2">Recolher todas as lidas</div>
                      </div>
                    </DropdownItem>

                    <DropdownItem
                      onClick={() => handleBotaoRecolherTodas(false)}
                      disabled={getIdsNotificacoesLidasPagina().length == 0}
                    >
                      <div className="d-flex">
                        <div>{ICONES_SETA.ABRIR_DETALHE}</div>
                        <div className="ms-2">Abrir todas as lidas</div>
                      </div>
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>
            </div>

            {/* BARRA DA AÇÃO EM ANDAMENTO (EXCLUIR / MARCAR COMO LIDO) */}
            {(acaoExcluir || acaoMarcarLido) && (
              <div
                className={
                  acaoExcluir
                    ? "notificacao-barra-acao notificacao-barra-excluir"
                    : "notificacao-barra-acao"
                }
              >
                <div className="d-flex align-items-center gap-2">
                  <UCheckbox
                    name="checkTodos"
                    onChange={handleChangeCheckAcaoTodos}
                    checked={checkAcaoTodos}
                  ></UCheckbox>
                  <span>
                    {acaoExcluir
                      ? "Selecione as notificações para excluir"
                      : "Selecione as notificações para marcar como lidas"}
                  </span>
                </div>
                <div>
                  <Label className="fw-bold me-2 mb-0">Selecionadas:</Label>
                  {countSelecionados}
                </div>
              </div>
            )}

            {/* LISTA DAS NOTIFICAÇÕES */}
            <div className="notificacao-lista">
              {result.items.map((nu, index) => (
                <div
                  key={index}
                  ref={(el) => (rowsRef.current[index] = el)}
                  tabIndex={0}
                  onClick={() => handleClickRow(index)}
                  onKeyDown={(event) => handleKeyDownRow(event)}
                  className={`notificacao-item cursor-default${
                    selectedRow === index ? " notificacao-item-selecionada" : ""
                  }${!nu.data_visualizacao ? " notificacao-item-nao-lida" : ""}`}
                >
                  <div
                    className={`notificacao-icone${
                      nu.data_visualizacao ? "" : " notificacao-icone-nao-lida"
                    }${getIconeNotificacao(nu).classe}`}
                  >
                    {getIconeNotificacao(nu).icone}
                  </div>

                  <div className="notificacao-conteudo">
                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <span className="notificacao-tipo">
                          {fn_capitalizarPalavra(
                            nu.notificacao.notificacao_tipo.nome,
                          )}
                        </span>
                        {!nu.data_visualizacao && (
                          <span className="notificacao-badge-nova">Nova</span>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        {nu.data_visualizacao && (
                          <span className="notificacao-lida-em">
                            {ICONES.CHECK} Lida em{" "}
                            {fn_formatarData(nu.data_visualizacao)}
                          </span>
                        )}
                        <span className="notificacao-data">
                          {fn_formatarDataHora(nu.notificacao.data)}
                        </span>

                        {nu.data_visualizacao && (
                          <Button
                            name={`btnRecolher${nu.id}`}
                            size="sm"
                            color="light"
                            className={`btn-icone-sm p-0${
                              isNotificacaoRecolhida(nu)
                                ? ""
                                : " notificacao-seta-aberta"
                            }`}
                            title={
                              isNotificacaoRecolhida(nu)
                                ? "Abrir a notificação"
                                : "Recolher a notificação"
                            }
                            onClick={(event) => handleClickRecolher(event, nu)}
                          >
                            {isNotificacaoRecolhida(nu)
                              ? ICONES_SETA.FECHAR_DETALHE
                              : ICONES_SETA.ABRIR_DETALHE}
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="notificacao-mensagem">
                      {isNotificacaoRecolhida(nu) ? (
                        <span>{nu.notificacao.titulo}...</span>
                      ) : (
                        <TextoFormatado texto={nu.notificacao.descricao} />
                      )}
                    </div>
                  </div>

                  {(acaoExcluir || acaoMarcarLido) && (
                    <div className="notificacao-check">
                      <UCheckbox
                        name={`check${nu.id}`}
                        onChange={(event) =>
                          handleChangeCheckAcaoInd(event, nu.id)
                        }
                        checked={checkAcao[nu.id] || false}
                        disabled={acaoMarcarLido && nu.data_visualizacao}
                      ></UCheckbox>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <Paginacao
              result={result}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
            />

            {/* ATALHOS DO TECLADO */}
            {!(acaoExcluir || acaoMarcarLido) && result.items.length > 0 && (
              <div className="notificacao-atalhos">
                <span>
                  <kbd>Enter</kbd> marca como lida / abre ou recolhe a lida
                </span>
                <span>
                  <kbd>Del</kbd> exclui
                </span>
                <span>
                  <kbd>↑</kbd> <kbd>↓</kbd> navegam entre as notificações
                </span>
              </div>
            )}
          </ModalBody>

          <ModalFooter>
            <div className="d-flex align-items-center justify-content-end">
              {(acaoExcluir || acaoMarcarLido) && (
                <Button
                  color={acaoExcluir ? "danger" : "primary"}
                  onClick={handleBotaoSalvarAcao}
                  className="me-2"
                >
                  {acaoExcluir ? (
                    <>{ICONES_BOTAO.EXCLUIR} Excluir</>
                  ) : (
                    <> {ICONES_BOTAO.SALVAR} Salvar</>
                  )}
                </Button>
              )}

              <Button color="secondary" onClick={handleBotaoFechar}>
                {ICONES_BOTAO.FECHAR} Fechar
              </Button>
            </div>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
}

export default TNotificacao_usuario;
